import grapesjs, { AssetManagerConfig } from 'grapesjs';
import BasicBlocks from 'grapesjs-blocks-basic';
import Flexbox from 'grapesjs-blocks-flexbox';
import Countdown from 'grapesjs-component-countdown';
import CustomCode from 'grapesjs-custom-code';
import Float from 'grapesjs-float';
import Navbar from 'grapesjs-navbar';
import PostCSSParser from 'grapesjs-parser-postcss';
import Forms from 'grapesjs-plugin-forms';
import grapesjsPresetWebpage from 'grapesjs-preset-webpage';
import Bg from 'grapesjs-style-bg';
import Filter from 'grapesjs-style-filter';
import Gradient from 'grapesjs-style-gradient';
import Tooltip from 'grapesjs-tooltip';
import Typed from 'grapesjs-typed';
import 'grapesjs/dist/css/grapes.min.css';
import React, { useEffect, useRef, useState } from 'react';
import { useMultiClientUploadFileMutation } from '../../Apis/Client/ClientFileUploadApi';
import { apiResponse } from '../../Interfaces';
import { MainLoader } from '../Common';
import { convertData, rtkErrorRead } from '../../Helper';

function GrapesEditor(props: GrapesEditorProps) {
  const { htmlCode, setHTMLCode, cssCode, setCSSCode, libraries } = props;

  const [uploadClientDoc] = useMultiClientUploadFileMutation();
  const [isLoading, setIsloading] = useState(false);

  const editorRef = useRef<HTMLDivElement>(null);
  const isFullAccess = false;

  useEffect(() => {
    if (editorRef.current) {
      // const assetManager: AssetManagerConfig = grapesjs

      const editor = grapesjs.init({
        container: editorRef.current,
        fromElement: true,
        width: 'auto',
        height: '100vh',
        storageManager: false,
        showDevices: isFullAccess,
        plugins: isFullAccess
          ? [
              grapesjsPresetWebpage,
              Forms,
              Navbar,
              Countdown,
              Gradient,
              Filter,
              Bg,
              Flexbox,
              BasicBlocks,
              Tooltip,
              CustomCode,
              Typed,
              PostCSSParser,
              Float,
            ]
          : [],
      });

      editor.on('asset:upload:end', async (asset) => {
        function base64ToFile(
          base64String: string,
          fileName: string,
          mimeType: string
        ) {
          const byteCharacters = atob(base64String.split(',')[1]);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const file = new File([byteArray], fileName, { type: mimeType });
          return file;
        }

        const filesArray = asset.data.map((item: any) => {
          return base64ToFile(item.src, item.name, 'image/jpeg');
        });

        // Upload the file using uploadFile function
        const uploadedPath = await uploadFile(filesArray);

        if (uploadedPath) {
          const data: { DocId: number; path: string }[] =
            convertData(uploadedPath);
          const newUrl = `${process.env.REACT_APP_APIBaseUrl}/uploads/${props.layoutStoreId}/${data[0].path}`;

          const selected = editor.getSelected();
          if (selected && selected.is('image')) {
            selected.set('src', newUrl);
          }
        }

        editor.AssetManager.close();
      });

      if (htmlCode) {
        const updatedHtml = htmlCode.replace(
          /<img\s+[^>]*src=["']([^"']+)["']/g,
          (match, src) => {
            return `<img src="${process.env.REACT_APP_APIBaseUrl}/uploads/${props.layoutStoreId!}/${src}" crossOrigin="use-credentials"`;
          }
        );

        editor.setComponents(updatedHtml, {});
      }
      editor.setStyle(cssCode);

      const loadCss = (doc: Document, url: string) => {
        const link = doc.createElement('link');
        link.rel = 'stylesheet';
        link.crossOrigin = 'use-credentials';
        link.href = `${process.env.REACT_APP_APIBaseUrl}/uploads/${props.layoutStoreId!}/${url}`;
        doc.head.appendChild(link);
      };

      const loadJs = (doc: Document, url: string) => {
        const script = doc.createElement('script');
        script.src = `${process.env.REACT_APP_APIBaseUrl}/uploads/${props.layoutStoreId!}/${url}`;
        script.crossOrigin = 'use-credentials';
        script.async = true;
        doc.body.appendChild(script);
      };

      const waitForIframe = () => {
        const iframe = editor.Canvas?.getFrameEl();
        if (iframe && iframe.contentDocument) {
          const iframeDocument = iframe.contentDocument;

          iframeDocument
            .querySelectorAll('link[rel="stylesheet"], script[src]')
            .forEach((el) => el.remove());

          libraries.forEach(({ url, type }) => {
            if (type === 'css') {
              loadCss(iframeDocument, url.trim());
            } else if (type === 'js') {
              loadJs(iframeDocument, url.trim());
            }
          });
        } else {
          setTimeout(waitForIframe, 500);
        }
      };

      editor.on('load', waitForIframe);

      if (!isFullAccess) {
        const Panels = ['commands', 'views', 'views-container'];
        Panels.forEach((obj) => editor.Panels.removePanel(obj));

        const buttons = ['sw-visibility', 'fullscreen', 'export-template'];
        buttons.forEach((obj) => editor.Panels.removeButton('options', obj));

        editor.on('load', function () {
          editor.runCommand('preview');

          const editorEl = editor.getEl()!;
          const pfx = editor.Config.stylePrefix;

          const button = document.getElementsByClassName(
            `${pfx}off-prv fa fa-eye-slash`
          )[0];
          editorEl.removeChild(button);

          const savebutton = document.createElement('span');
          savebutton.className = `${pfx}off-prv fa fa-save`;
          savebutton.title = 'Save Draft';
          savebutton.style.fontSize = '20px';
          savebutton.style.padding = '15px';
          savebutton.style.cursor = 'pointer';
          savebutton.style.margin = '5px';
          editorEl.appendChild(savebutton);
          savebutton.onclick = () => editor.runCommand('save-draft');
        });
      }

      editor.Panels.addButton('options', {
        id: 'save-draft',
        className: 'fa fa-save',
        command: 'save-draft',
        attributes: { title: 'Save Draft' },
      } as any);

      editor.Commands.add(
        'save-draft',
        (editor: any, sender?: any, opts?: any) => {
          const updatedHtml = editor.getHtml();
          const updatedCss = editor.getCss();
          if (updatedHtml !== htmlCode) {
            setHTMLCode(
              cleanImageTags(updatedHtml.replace(/<body[^>]*>|<\/body>/g, ''))
            );
          }
          if (updatedCss !== cssCode) setCSSCode(updatedCss || '');
        }
      );

      const cleanImageTags = (html: string) => {
        const updatedHtml = html.replace(
          new RegExp(
            `<img\\s+[^>]*src="${`${process.env.REACT_APP_APIBaseUrl}/uploads/${props.layoutStoreId}/`}([^"]+)"[^>]*crossorigin="use-credentials"([^>]*>)`,
            'g'
          ),
          (match, p1, otherAttrs) => {
            return `<img src="${p1}"${otherAttrs}`;
          }
        );

        return updatedHtml;
      };

      return () => {
        editor.destroy();
      };
    }
  }, [htmlCode, cssCode, libraries]);

  async function uploadFile(files: File[]): Promise<string> {
    setIsloading(true);

    const formData = new FormData();

    files.forEach((file) => {
      formData.append(`FileDetails`, file);
    });
    formData.append('folderPath', 'files');
    formData.append('LayoutStoreId', props.layoutStoreId?.toString()!);

    try {
      const response: apiResponse = await uploadClientDoc({
        formData,
        onUploadProgress: (event) => {},
      });

      setIsloading(false);
      if (response.error) {
        rtkErrorRead(response.error);
        return '';
      } else {
        return response.data?.result;
      }
    } catch (error) {
      console.error('Upload failed:', error);
      return ''; // Return empty string on failure
    }
  }

  return (
    <>
      {isLoading && <MainLoader />}
      <div ref={editorRef} />
    </>
  );
}

interface GrapesEditorProps {
  htmlCode: string | null;
  setHTMLCode: React.Dispatch<React.SetStateAction<string | null>>;
  cssCode: string | null;
  setCSSCode: React.Dispatch<React.SetStateAction<string | null>>;
  libraries: { url: string; type: 'css' | 'js' }[];
  layoutStoreId?: number;
}

export default GrapesEditor;
